import React, { useCallback, useEffect, useMemo, useState } from "react";
import PieChart from "./PieChart";
import { Col, Divider, Flex, Row, Select} from "antd";
import BarChart from "./BarChart";
import { getCookie } from "general_function";
import API from "API";
import FullMask from "components/layout/FullMask";

function Dashboard({ selectedTerm, user }) {
    const [dataChart, setDataChart] = useState([]);
    const [dataSearch, setDataSearch] = useState([]);
    const [searchValue, setSearchValue] = useState(null);
    const [courseChosen, setCourseChosen] = useState(null);
    const [loading, setLoading] = useState(true);

    const labelsBarChart = useMemo(() => {
        return dataChart?.map(({ name }) => name);
    }, [dataChart]);

    const labelsBarChartSearch = useMemo(() => {
        return dataSearch?.map((_, index) => `Bài ${index+1}`);
    }, [dataSearch]);

    const quizzData = useMemo(() => {
        const allQuizzData = dataChart.reduce(
            (prev, value) => {
                prev.total += value.quizzes?.total;
                prev.correct += value.quizzes?.correct;
                prev.wrong += value.quizzes?.wrong;

                return prev;
            },
            { remaining: 0, total: 0, correct: 0, wrong: 0 }
        );
        allQuizzData.remaining =
            allQuizzData.total - allQuizzData.correct - allQuizzData.wrong;

        delete allQuizzData.total;
        return Object.entries(allQuizzData).map((values) => values[1]);
    }, [dataChart]);

    const essaysData = useMemo(() => {
        const allEssaysData = dataChart.reduce(
            (prev, value) => {
                prev.total += value.essays?.total;
                prev.done += value.essays?.done;
                return prev;
            },
            { remaining: 0, total: 0, done: 0 }
        );
        allEssaysData.remaining = allEssaysData.total - allEssaysData.done;
        delete allEssaysData.total;
        return Object.entries(allEssaysData).map((values) => values[1]);
    }, [dataChart]);

    const datasets = useMemo(() => {
        const dataAll =
            dataChart &&
            dataChart.reduce(
                (prev, value) => {
                    prev?.[0].push(
                        value.quizzes?.total -
                            value.quizzes?.correct -
                            value.quizzes?.wrong
                    );
                    prev?.[1].push(value.quizzes?.correct);
                    prev?.[2].push(value.quizzes?.wrong);

                    prev?.[3].push(
                        value.chatbot?.capacity - value.chatbot?.chatbot_usage
                    );
                    prev?.[4].push(value.chatbot?.chatbot_usage);
                    prev?.[5].push(value.essays?.total - value.essays?.done);
                    prev?.[6].push(value.essays?.done);
                    return prev;
                },
                Array.from({ length: 7 }, () => [])
            );
        if (!dataChart.length) return [];

        return [
            {
                label: "Chưa làm",
                data: dataAll?.[0],
                backgroundColor: "rgb(0, 176, 255)",
                stack: "Trắc nghiệm",
            },
            {
                label: "Đúng",
                data: dataAll?.[1],
                backgroundColor: "rgb(34, 197, 94)", 
                stack: "Trắc nghiệm",
            },
            {
                label: "Sai",
                data: dataAll?.[2],
                backgroundColor: "rgb(239, 68, 68)",
                stack: "Trắc nghiệm",
            },
            {
                label: "Chưa làm",
                data: dataAll?.[5],
                backgroundColor: "rgb(255, 206, 86)",
                stack: "Tự luận",
            },
            {
                label: "Đã làm",
                data: dataAll?.[6],
                backgroundColor: "rgb(54, 162, 235)",
                stack: "Tự luận",
            },
        ];
    }, [dataChart]);

    const dataChartBarSearch = useMemo(() => {
        const dataAll =
            dataSearch &&
            dataSearch.reduce(
                (prev, value) => {
                    prev?.[0].push(
                        value.quizzes?.total -
                            value.quizzes?.correct -
                            value.quizzes?.wrong
                    );
                    prev?.[1].push(value.quizzes?.correct);
                    prev?.[2].push(value.quizzes?.wrong);
                    prev?.[3].push(value.essays?.total - value.essays?.done);
                    prev?.[4].push(value.essays?.done);
                    return prev;
                },
                Array.from({ length: 5 }, () => [])
            );
        if (!dataSearch.length) return [];

        return [
            {
                label: "Chưa làm",
                data: dataAll?.[0],
                backgroundColor: "rgb(0, 176, 255)",
                stack: "Trắc nghiệm",
            },
            {
                label: "Đúng",
                data: dataAll?.[1],
                backgroundColor: "rgb(34, 197, 94)", 
                stack: "Trắc nghiệm",
            },
            {
                label: "Sai",
                data: dataAll?.[2],
                backgroundColor: "rgb(239, 68, 68)",
                stack: "Trắc nghiệm",
            },
            {
                label: "Chưa làm",
                data: dataAll?.[3],
                backgroundColor: "rgb(255, 206, 86)",
                stack: "Tự luận",
            },
            {
                label: "Đã làm",
                data: dataAll?.[4],
                backgroundColor: "rgb(54, 162, 235)",
                stack: "Tự luận",
            },
        ];
    }, [dataSearch]);

    const getDashboardStatistics = useCallback(async (setData, courseId) => {
        if (!getCookie("usertoken")) {
            return;
        }
        setLoading(true);
        try {
            if (courseId) {
                const { data } = await API.post("/student/dashboard-statistics/", {
                    course_id: courseId,
                    term_id: selectedTerm.id,
                    user: user
                });
                setData(data);
            } else {
                const { data } = await API.post("/student/dashboard-statistics/", {
                    term_id: selectedTerm.id,
                    user: user
                });
                setData(data);
            }
        } catch (error) {
            console.log(error);
        } finally {
            setLoading(false);
        }
    },[selectedTerm.id, user]);

    const handleChange = (value, item) => {
        setCourseChosen(item ? item.label : null);
        setSearchValue(value);
    };

    const options = useMemo(() => {
        return dataChart?.map(({ name, id }) => ({
            label: name,
            value: id,
        }));
    }, [dataChart]);

    useEffect(() => {
        if (searchValue) {
            getDashboardStatistics(setDataSearch, searchValue);
        }
    }, [selectedTerm, searchValue, getDashboardStatistics]);

    useEffect(() => {
        getDashboardStatistics(setDataChart);
    }, [selectedTerm, getDashboardStatistics]);

    return (
        <div className="mx-auto" style={{ position: "relative" }}>
            {loading && <FullMask page/>}
            <Row>
                <Col md={4}/>
                <Col xs={24} md={8}>
                    <div>
                        <PieChart
                            dataValues={quizzData}
                            labels={["Chưa làm", "Đúng", "Sai"]}
                            label="Trắc nghiệm"
                            title="Trắc nghiệm"
                            backgroundColor={[
                                "rgb(0, 176, 255)",
                                "rgb(34, 197, 94)",
                                "rgb(239, 68, 68)",
                            ]}
                        />
                    </div>
                </Col>
                <Col xs={24} md={8}>
                    <div>
                        <PieChart
                            dataValues={essaysData}
                            labels={["Chưa làm", "Đã làm"]}
                            label="Tự luận"
                            title="Tự luận"
                            backgroundColor={[
                                "rgb(255, 206, 86)",
                                "rgb(54, 162, 235)",
                            ]}
                        />
                    </div>
                </Col>
                <Col md={4}/>
            </Row>

            <div className="mt-4" style={{
                overflowX: "auto"
            }}>
                <BarChart
                    datasets={datasets}
                    labels={labelsBarChart}
                    title="CÁC MÔN HỌC CỦA BẠN"
                />
            </div>

            <Divider />

            <div>
                <Flex wrap gap="small">
                    <Select
                        style={{ width: "200px" }}
                        placeholder="Chọn môn học"
                        onChange={handleChange}
                        value={searchValue}
                        options={options}
                        filterSort={(optionA, optionB) =>
                            (optionA?.label ?? "")
                                .toLowerCase()
                                .localeCompare(
                                    (optionB?.label ?? "").toLowerCase()
                                )
                        }
                        filterOption={(input, option) =>
                            (option?.label ?? "")
                                .toLowerCase()
                                .includes(input.toLowerCase())
                        }
                        showSearch
                        allowClear
                        onClear={() => {
                            setDataSearch([]);
                            setSearchValue();
                        }}
                    />
                </Flex>
            </div>

            {searchValue && <div className="mt-4" style={{
                overflowX: "auto"
            }}>
                <BarChart
                    datasets={dataChartBarSearch}
                    labels={labelsBarChartSearch}
                    title={courseChosen}
                />
            </div>}
        </div>
    );
}

export default Dashboard;
